import { useState } from "react";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

export default function Account() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (form.new_password !== form.confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">My Account</h1>
        <p className="text-slate-500 text-sm mt-1">
          Signed in as <span className="font-medium text-slate-700">{user?.email}</span>
        </p>
      </div>

      <Card className="p-6 rounded-lg border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center">
            <KeyRound className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-semibold text-slate-900">Change Password</div>
            <div className="text-xs text-slate-400">Set your own private password.</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div>
            <Label>Current Password</Label>
            <Input
              type="password"
              data-testid="current-password-input"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="mt-1 rounded-md"
              required
            />
          </div>
          <div>
            <Label>New Password</Label>
            <Input
              type="password"
              data-testid="new-password-input"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="mt-1 rounded-md"
              placeholder="At least 6 characters"
              required
            />
          </div>
          <div>
            <Label>Confirm New Password</Label>
            <Input
              type="password"
              data-testid="confirm-password-input"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="mt-1 rounded-md"
              required
            />
          </div>
          <Button type="submit" data-testid="save-password-btn" disabled={saving} className="rounded-md bg-slate-900 hover:bg-slate-800">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Update Password
          </Button>
        </form>
      </Card>
    </div>
  );
}
